import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'es', {style:'currency',currency:'EUR'}).format(amount / 100);
const config = () => document.getElementById('aw-aov-config');
let currentCart;
let pendingFetch;
let fetchRevision = 0;

function remaining(total, threshold) {
  return Math.max(0, threshold - total);
}

function contributionRate(cart) {
  let rate = 1;
  if (!cart.total_discount) return rate;
  const discounts = cart.cart_level_discount_applications || [];
  const allocated = discounts.reduce((sum, discount) => sum + Number(discount.total_allocated_amount || 0), 0);
  if (!discounts.length || allocated !== cart.total_discount) return null;
  for (const discount of discounts) {
    if (discount.value_type !== 'percentage' || discount.target_selection !== 'all') return null;
    rate *= 1 - Number(discount.value) / 100;
  }
  return rate > 0 ? rate : null;
}

function selectSuggestions(items, cart, threshold, useShipping) {
  if (!cart.item_count) return [];
  const missing = remaining(cart.total_price, threshold);
  const rate = useShipping ? contributionRate(cart) : 1;
  const ids = new Set(cart.items.map(item => String(item.product_id)));
  const available = items.filter(item => !ids.has(String(item.id))).sort((a,b) => a.price - b.price);
  const qualifying = useShipping && missing && rate !== null
    ? available.filter(item => Math.floor(item.price * rate) >= missing) : [];
  const preferred = qualifying.length ? qualifying : available;
  return [...preferred, ...available.filter(item => !preferred.includes(item))].slice(0,4);
}

class AuraShippingProgress extends HTMLElement {
  connectedCallback() {
    this.update(currentCart || {total_price:Number(this.dataset.total),item_count:Number(this.dataset.count),currency:'EUR'});
  }
  update(cart) {
    this.hidden = cart.currency !== 'EUR';
    if (this.hidden) return;
    const threshold = Number(this.dataset.threshold);
    const missing = remaining(cart.total_price, threshold);
    const text = !cart.item_count ? this.dataset.empty.replace('{X}', money(threshold)) : missing ? this.dataset.missing.replace('{X}', money(missing)) : this.dataset.success;
    const message = this.querySelector('.aw-shipping-message');
    if (message && message.textContent !== text) message.textContent = text;
    const progress = this.querySelector('progress');
    if (progress) {progress.max = threshold;progress.value = Math.min(threshold,cart.total_price);}
  }
}

class AuraCartAddons extends HTMLElement {
  connectedCallback() {if(currentCart) this.update(currentCart);}
  update(cart) {
    const cfg = config();
    const source = document.getElementById('aw-addon-products');
    if (!cfg || !source) {this.hidden = true;return;}
    const shipping = cfg.dataset.shipping === 'true' && cfg.dataset.country === 'ES' && cart.currency === 'EUR';
    const options = [...source.content.querySelectorAll('.aw-addon-card')].map(node=>({id:node.dataset.productId,price:Number(node.dataset.price),node}));
    const chosen = selectSuggestions(options,cart,Number(cfg.dataset.threshold),shipping);
    this.hidden = !chosen.length;
    const rate = contributionRate(cart);
    const missing = remaining(cart.total_price, Number(cfg.dataset.threshold));
    const allQualify = shipping && missing > 0 && rate !== null && chosen.every(item => Math.floor(item.price * rate) >= missing);
    this.querySelector('h3').textContent = allQualify ? this.dataset.heading : this.dataset.generic;
    const grid = this.querySelector('.aw-addon-grid');
    const signature = chosen.map(item=>item.id).join(',');
    if (grid.dataset.signature !== signature) {
      grid.replaceChildren(...chosen.map(item=>item.node.cloneNode(true)));
      grid.dataset.signature = signature;
    }
  }
}

function applyCart(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  currentCart = cart;
  document.querySelectorAll('aw-shipping-progress,aw-cart-addons').forEach(element=>element.update?.(cart));
}

function fetchCart() {
  if (pendingFetch) return pendingFetch;
  const revision = fetchRevision;
  pendingFetch = fetch((window.Shopify?.routes?.root || '/')+'cart.js',{credentials:'same-origin',headers:{Accept:'application/json'}})
    .then(response=>{if(!response.ok) throw new Error('cart');return response.json();})
    .then(cart=>{if(revision===fetchRevision) applyCart(cart);})
    .catch(()=>{})
    .finally(()=>{pendingFetch=null;});
  return pendingFetch;
}

customElements.define('aw-shipping-progress',AuraShippingProgress);
customElements.define('aw-cart-addons',AuraCartAddons);
try {applyCart(JSON.parse(document.querySelector('[data-aw-cart-initial]')?.textContent || 'null'));} catch {}
document.addEventListener('aw:cart-updated',event=>{
  fetchRevision++;
  applyCart(event.detail.cart);
  if(event.detail.changed) {
    const page = document.querySelector('cart-items-component:not([data-drawer])');
    if(page?.dataset.sectionId) sectionRenderer.renderSection(page.dataset.sectionId,{cache:false,mode:'full'}).catch(()=>{});
  }
});
for(const eventName of [StandardEvents.cartLinesUpdate,StandardEvents.cartDiscountUpdate]) {
  document.addEventListener(eventName,event=>{
    if(event.promise) event.promise.then(()=>fetchCart()).catch(()=>{});
    else fetchCart();
  });
}
window.addEventListener('pageshow',event=>{if(event.persisted) fetchCart();});
document.addEventListener('shopify:section:load',()=>{
  try {applyCart(JSON.parse(document.querySelector('[data-aw-cart-initial]')?.textContent || 'null'));} catch {}
  fetchCart();
});
